import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import ErrorBoundary from "@/components/ErrorBoundary";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import AdvancedBooking from "@/pages/AdvancedBooking";
import CurrentBooking from "@/pages/CurrentBooking";
import PendingApprovals from "@/pages/PendingApprovals";
import TicketIssue from "@/pages/TicketIssue";
import Verification from "@/pages/Verification";
import Transactions from "@/pages/Transactions";
import DataManager from "./pages/DataManager";
import LoginLogs from "./pages/LoginLogs";
import Reports from "@/pages/Reports";
import Masters from "@/pages/Masters";
import SareeRequest from "@/pages/SareeRequest";
import SareeGodownIn from "@/pages/SareeGodownIn";
import SareeGodownOut from "@/pages/SareeGodownOut";
import SareeReports from "@/pages/SareeReports";
import SareeSalesCounter from "@/pages/SareeSalesCounter";
import SareeDashboard from "@/pages/SareeDashboard";
import AnnaIssue from "@/pages/AnnaIssue";
import AnnaVerify from "@/pages/AnnaVerify";
import AnnaReports from "@/pages/AnnaReports";
import AnnaDashboard from "@/pages/AnnaDashboard";
import HundialEntry from "@/pages/HundialEntry";
import HundialDashboard from "@/pages/HundialDashboard";
import IdentityDashboard from "@/pages/IdentityDashboard";
import IdentityPasses from "@/pages/IdentityPasses";
import IdentityApprovals from "@/pages/IdentityApprovals";
import IdentityMasters from "@/pages/IdentityMasters";
import IdentityReports from "@/pages/IdentityReports";
import IdentityScan from "@/pages/IdentityScan";
import IdentityAssignTag from "@/pages/IdentityAssignTag";
import HundialReports from "@/pages/HundialReports";
import ShiftClosing from "@/pages/ShiftClosing";
import { userPages } from "@/lib/pages";

function RequireAuth({ children }) {
  const { user } = useAuth();
  if (user === undefined)
    return (
      <div className="min-h-screen flex items-center justify-center bg-cream">
        <div className="text-center">
          <div className="w-10 h-10 border-4 border-gold-400 border-t-maroon-700 rounded-full animate-spin mx-auto" />
          <p className="mt-4 text-sm text-maroon-800 font-heading">Loading…</p>
        </div>
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

// Home: the Protocol Dashboard when ticked; otherwise Layout opens the user's first page.
function Home() {
  const { user } = useAuth();
  return userPages(user).includes("PROTOCOL_DASHBOARD") ? <Dashboard /> : null;
}

function RequirePage({ page, children }) {
  const { user } = useAuth();
  const need = Array.isArray(page) ? page : [page]; // several pages: any one of them is enough
  if (user && !need.some((p) => userPages(user).includes(p))) return <Navigate to="/" replace />;
  return children;
}

// The counter's direct-print desktop shortcut opens the site with ?directprint=1 (Chrome --kiosk-printing).
// Remember it in that window's own Chrome profile, before any login redirect drops the query.
try {
  const q = new URLSearchParams(window.location.search);
  if (q.get("directprint") === "1") localStorage.setItem("pd_direct_print", "1");
  if (["s21x15", "a5", "half", "a4", "4x6", "card", "rot", "slip"].includes(q.get("paper"))) localStorage.setItem("pd_print_paper", q.get("paper"));
} catch { /* storage unavailable */ }

export default function App() {
  return (
    <ErrorBoundary>
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route element={<RequireAuth><Layout /></RequireAuth>}>
            <Route path="/" element={<Home />} />
            <Route path="/dashboard" element={<Home />} />
            <Route path="/protocol/advanced" element={<RequirePage page="ADVANCED"><AdvancedBooking /></RequirePage>} />
            <Route path="/protocol/current" element={<RequirePage page="CURRENT"><CurrentBooking /></RequirePage>} />
            <Route path="/protocol/pending" element={<RequirePage page="PENDING"><PendingApprovals /></RequirePage>} />
            <Route path="/protocol/issue" element={<RequirePage page="ISSUE"><TicketIssue /></RequirePage>} />
            <Route path="/protocol/verify" element={<RequirePage page="VERIFY"><Verification /></RequirePage>} />
            <Route path="/protocol/transactions" element={<RequirePage page="TRANSACTIONS"><Transactions /></RequirePage>} />
            <Route path="/masters" element={<Masters />} />
            <Route path="/reports" element={<RequirePage page="PROTOCOL_REPORTS"><Reports /></RequirePage>} />
            <Route path="/protocol/shift-closing" element={<RequirePage page="PROTOCOL_SHIFT_CLOSING"><ShiftClosing module="PROTOCOL" key="PROTOCOL" /></RequirePage>} />
            <Route path="/sarees/shift-closing" element={<RequirePage page="SAREE_SHIFT_CLOSING"><ShiftClosing module="SAREES" key="SAREES" /></RequirePage>} />
            <Route path="/data-manager" element={<DataManager />} />
            <Route path="/login-logs" element={<LoginLogs />} />
            <Route path="/sarees/request" element={<RequirePage page="SAREE_REQUEST"><SareeRequest /></RequirePage>} />
            <Route path="/sarees/godown-in" element={<RequirePage page="SAREE_GODOWN_IN"><SareeGodownIn /></RequirePage>} />
            <Route path="/sarees/godown-out" element={<RequirePage page="SAREE_GODOWN_OUT"><SareeGodownOut /></RequirePage>} />
            <Route path="/sarees/reports" element={<RequirePage page="SAREE_REPORTS"><SareeReports /></RequirePage>} />
            <Route path="/sarees/sales" element={<RequirePage page="SAREE_SALES"><SareeSalesCounter /></RequirePage>} />
            <Route path="/sarees/devasthanam-sales" element={<RequirePage page="SAREE_DEVASTHANAM"><SareeSalesCounter title="Sarees Devasthanam Sales" channel="DEVASTHANAM" /></RequirePage>} />
            <Route path="/sarees/dashboard" element={<RequirePage page="SAREE_DASHBOARD"><SareeDashboard /></RequirePage>} />
            <Route path="/anna/dashboard" element={<RequirePage page="ANNA_DASHBOARD"><AnnaDashboard /></RequirePage>} />
            <Route path="/anna/issue" element={<RequirePage page="ANNA_ISSUE"><AnnaIssue /></RequirePage>} />
            <Route path="/anna/verify" element={<RequirePage page="ANNA_VERIFY"><AnnaVerify /></RequirePage>} />
            <Route path="/anna/reports" element={<RequirePage page="ANNA_REPORTS"><AnnaReports /></RequirePage>} />
            <Route path="/identity/dashboard" element={<RequirePage page="ID_DASHBOARD"><IdentityDashboard /></RequirePage>} />
            <Route path="/identity/duty" element={<RequirePage page="ID_DUTY"><IdentityPasses key="duty" kind="duty" /></RequirePage>} />
            <Route path="/identity/vehicle" element={<RequirePage page="ID_VEHICLE"><IdentityPasses key="vehicle" kind="vehicle" /></RequirePage>} />
            <Route path="/identity/children" element={<RequirePage page="ID_CHILDREN"><IdentityPasses key="children" kind="children" /></RequirePage>} />
            <Route path="/identity/pandits" element={<RequirePage page="ID_PANDITS"><IdentityPasses key="pandit" kind="pandit" /></RequirePage>} />
            <Route path="/identity/assign-tag" element={<RequirePage page="ID_CHILDREN"><IdentityAssignTag /></RequirePage>} />
            <Route path="/identity/scan" element={<RequirePage page="ID_SCAN"><IdentityScan /></RequirePage>} />
            <Route path="/identity/approvals" element={<RequirePage page="ID_APPROVALS"><IdentityApprovals /></RequirePage>} />
            <Route path="/identity/departments" element={<RequirePage page="ID_MASTERS"><IdentityMasters key="d" section="departments" /></RequirePage>} />
            <Route path="/identity/locations" element={<RequirePage page="ID_MASTERS"><IdentityMasters key="l" section="locations" /></RequirePage>} />
            <Route path="/identity/sacred-locations" element={<RequirePage page="ID_MASTERS"><IdentityMasters key="s" section="sacred" /></RequirePage>} />
            <Route path="/identity/tag-ranges" element={<RequirePage page="ID_MASTERS"><IdentityMasters key="t" section="tag-ranges" /></RequirePage>} />
            <Route path="/identity/reports" element={<RequirePage page="ID_REPORTS"><IdentityReports /></RequirePage>} />
            <Route path="/hundial/dashboard" element={<RequirePage page="HUNDIAL_DASHBOARD"><HundialDashboard /></RequirePage>} />
            <Route path="/hundial/entry" element={<RequirePage page="HUNDIAL_ENTRY"><HundialEntry /></RequirePage>} />
            <Route path="/hundial/reports" element={<RequirePage page="HUNDIAL_REPORTS"><HundialReports /></RequirePage>} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster richColors position="top-right" />
    </AuthProvider>
    </ErrorBoundary>
  );
}
